import axios from 'axios';
import { API_CONFIG } from 'api/config';
import auth from 'api/core/auth';

class HttpClient {
  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request Interceptor
    this.instance.interceptors.request.use(
      config => {
        if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
          const csrfToken = this.getCookie('csrftoken');
          if (csrfToken) {
            config.headers['X-CSRFToken'] = csrfToken;
          }
        }

        config.withCredentials = true;

        return config;
      },
      error => Promise.reject(error),
    );

    // Response Interceptor
    this.instance.interceptors.response.use(
      response => {
        if (API_CONFIG.SUCCESS_STATUS_CODES.includes(response.status)) {
          return response.data;
        }
        return response;
      },
      error => {
        if (error.response) {
          if (
            error.response.status === 401 ||
            (error.response.status === 403 &&
              !error.config.url.includes('/logout/'))
          ) {
            auth.clearAuth();

            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }

            return Promise.reject(
              new Error(
                error.response.status === 401
                  ? API_CONFIG.ERROR_MESSAGES.UNAUTHORIZED
                  : API_CONFIG.ERROR_MESSAGES.FORBIDDEN,
              ),
            );
          }

          switch (error.response.status) {
            case 400:
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.BAD_REQUEST),
              );
            case 404:
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.NOT_FOUND),
              );
            case 500:
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.SERVER),
              );
            default:
              return Promise.reject(
                new Error(API_CONFIG.ERROR_MESSAGES.DEFAULT),
              );
          }
        }

        if (error.request) {
          return Promise.reject(new Error(API_CONFIG.ERROR_MESSAGES.NETWORK));
        }

        return Promise.reject(error);
      },
    );
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  get(url, config = {}) {
    return this.instance.get(url, config);
  }

  post(url, data, config = {}) {
    return this.instance.post(url, data, config);
  }

  put(url, data, config = {}) {
    return this.instance.put(url, data, config);
  }

  patch(url, data, config = {}) {
    return this.instance.patch(url, data, config);
  }

  delete(url, config = {}) {
    return this.instance.delete(url, config);
  }
}

export default new HttpClient();
